/**
 * Bulk headshot upload.
 *
 * Photos are collected centrally at info@ and arrive as a folder of images named by whoever
 * took them. This pairs each file with a student by filename, shows the admin every pairing
 * it made, and only uploads what they confirm.
 *
 * The review step is not politeness. A headshot attached to the wrong student is worse than
 * no headshot at all — it would actively vouch for the wrong child at the door — so nothing
 * here writes on a guess.
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, ImageOff, Trash2, Upload, AlertTriangle } from 'lucide-react';
import { supabase } from '../supabase';
import { matchFilenameToStudents, cleanedStem, type MatchableStudent } from '../../utils/studentPhotoName';
import {
  STUDENT_PHOTO_BUCKET,
  headshotObjectPath,
  toSquareHeadshot,
} from '../../utils/studentPhotoImage';
import { analytics } from '../../utils/analytics';

interface PhotoStudent extends MatchableStudent {
  photo_path: string | null;
  metadata: { grade?: string; school?: string } | null;
}

type RowStatus = 'pending' | 'uploading' | 'done' | 'error';

interface PhotoRow {
  key: string;
  file: File;
  previewUrl: string;
  /** Who this file will be attached to. Seeded from the filename match, admin-editable. */
  studentId: string | null;
  /** True when the filename did not match exactly — surfaced so the admin looks twice. */
  fuzzy: boolean;
  ambiguousIds: string[];
  status: RowStatus;
  error?: string;
}

const ACCEPTED = 'image/jpeg,image/png,image/webp,image/heic';

/**
 * Supabase reports failures two different ways: a `StorageError` is a real `Error`, while a
 * PostgREST failure is a plain object carrying `message`. Testing only for `Error` would
 * collapse every database error in this flow into a useless "Upload failed", which is exactly
 * the case an admin most needs the detail for.
 */
function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const message = (e as { message: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return 'Upload failed.';
}

function studentLabel(s: PhotoStudent): string {
  const grade = s.metadata?.grade ? ` · ${s.metadata.grade}` : '';
  return `${s.last_name}, ${s.first_name}${grade}`;
}

export function StudentPhotoImport() {
  const [students, setStudents] = useState<PhotoStudent[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PhotoRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('students')
        .select('id, first_name, last_name, photo_path, metadata')
        .eq('active', true)
        .order('last_name');
      if (cancelled) return;
      if (error) setLoadError(error.message);
      else setStudents((data ?? []) as PhotoStudent[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Object URLs are a leak if they outlive their row, and a batch can be a few hundred.
  useEffect(() => {
    return () => { rows.forEach(r => URL.revokeObjectURL(r.previewUrl)); };
    // Intentionally on unmount only — per-row revocation happens where rows are removed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const studentById = useMemo(
    () => new Map(students.map(s => [s.id, s])),
    [students],
  );

  function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const added: PhotoRow[] = Array.from(fileList)
      .filter(f => f.type.startsWith('image/'))
      .map((file, i) => {
        const match = matchFilenameToStudents(file.name, students);
        return {
          key: `${file.name}-${file.size}-${Date.now()}-${i}`,
          file,
          previewUrl: URL.createObjectURL(file),
          studentId: match.kind === 'matched' ? match.studentId : null,
          fuzzy: match.kind === 'matched' && match.fuzzy,
          ambiguousIds: match.kind === 'ambiguous' ? match.studentIds : [],
          status: 'pending' as const,
        };
      });
    setRows(prev => [...prev, ...added]);
  }

  function removeRow(key: string) {
    setRows(prev => {
      const row = prev.find(r => r.key === key);
      if (row) URL.revokeObjectURL(row.previewUrl);
      return prev.filter(r => r.key !== key);
    });
  }

  function assign(key: string, studentId: string | null) {
    setRows(prev =>
      prev.map(r =>
        r.key === key ? { ...r, studentId, fuzzy: false, ambiguousIds: [], error: undefined } : r,
      ),
    );
  }

  /**
   * Two files pointed at the same student. Almost always a duplicate photo of one child
   * rather than two children — uploading both would just race, so the admin picks.
   */
  const duplicateStudentIds = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of rows) {
      if (!r.studentId || r.status === 'done') continue;
      seen.set(r.studentId, (seen.get(r.studentId) ?? 0) + 1);
    }
    return new Set([...seen.entries()].filter(([, n]) => n > 1).map(([id]) => id));
  }, [rows]);

  const readyRows = rows.filter(
    r => r.studentId && r.status === 'pending' && !duplicateStudentIds.has(r.studentId),
  );
  const unmatchedCount = rows.filter(r => !r.studentId && r.status !== 'done').length;
  const doneCount = rows.filter(r => r.status === 'done').length;

  /**
   * Upload, then repoint, then clean up — in that order.
   *
   * If any step fails the student keeps whatever photo they already had: the new object is
   * removed again on a failed write, and the old object is only deleted once the row is
   * definitely pointing somewhere else.
   */
  async function uploadRow(row: PhotoRow): Promise<void> {
    const studentId = row.studentId;
    if (!studentId) return;

    const blob = await toSquareHeadshot(row.file);
    const path = headshotObjectPath(studentId);

    const { error: uploadError } = await supabase.storage
      .from(STUDENT_PHOTO_BUCKET)
      .upload(path, blob, { contentType: 'image/webp', cacheControl: '3600' });
    if (uploadError) throw uploadError;

    const previousPath = studentById.get(studentId)?.photo_path ?? null;

    const { error: dbError } = await supabase
      .from('students')
      .update({ photo_path: path })
      .eq('id', studentId);
    if (dbError) {
      await supabase.storage.from(STUDENT_PHOTO_BUCKET).remove([path]);
      throw dbError;
    }

    // Best effort: an orphaned object costs a few kilobytes, and failing the row here would
    // wrongly suggest the new photo did not take.
    if (previousPath && previousPath !== path) {
      await supabase.storage.from(STUDENT_PHOTO_BUCKET).remove([previousPath]);
    }

    setStudents(prev =>
      prev.map(s => (s.id === studentId ? { ...s, photo_path: path } : s)),
    );
  }

  async function handleUploadAll() {
    setIsUploading(true);
    let uploaded = 0;
    let failed = 0;
    // Sequential on purpose: a batch is a few hundred small files, and a steady march with
    // visible per-row progress is easier to trust than a burst that fails halfway.
    for (const row of readyRows) {
      setRows(prev => prev.map(r => (r.key === row.key ? { ...r, status: 'uploading' } : r)));
      try {
        await uploadRow(row);
        uploaded++;
        setRows(prev => prev.map(r => (r.key === row.key ? { ...r, status: 'done' } : r)));
      } catch (e) {
        failed++;
        setRows(prev =>
          prev.map(r =>
            r.key === row.key ? { ...r, status: 'error', error: errorMessage(e) } : r,
          ),
        );
      }
    }
    setIsUploading(false);

    // One event for the batch, not one per child: a run is a few hundred files, and the
    // question worth answering later is "when did headshots last get imported, and how many".
    if (uploaded > 0 || failed > 0) {
      analytics.trackAdminAction('admin_student_photo_import', {
        entityType: 'student',
        metadata: { uploaded, failed, attempted: readyRows.length },
      });
    }
  }

  const withPhoto = students.filter(s => s.photo_path).length;

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading students…</div>;
  }
  if (loadError) {
    return <div className="p-6 text-sm text-red-600">Could not load students: {loadError}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-base font-semibold text-gray-900">Student headshots</h2>
        <p className="mt-1 text-sm text-gray-600">
          Photos appear on the teacher pickup roster and check-out sheet, so a substitute can
          confirm who they are releasing. They are stored privately and are not shown to parents.
        </p>
        <p className="mt-2 text-sm text-gray-500">
          Name each file <code className="px-1 py-0.5 bg-gray-100 rounded">First_Last.jpg</code>.
          {' '}Separators, capitalisation and name order are all forgiven — every pairing is shown
          below for you to confirm before anything uploads.
        </p>
        <p className="mt-2 text-xs text-gray-400">
          {withPhoto} of {students.length} active students have a headshot on file.
        </p>

        <label className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-blue-700">
          <Upload className="h-4 w-4" />
          Choose photos
          <input
            type="file"
            multiple
            accept={ACCEPTED}
            className="hidden"
            onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
          />
        </label>
      </div>

      {rows.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-600">
              {rows.length} file{rows.length === 1 ? '' : 's'} · {readyRows.length} ready
              {unmatchedCount > 0 && ` · ${unmatchedCount} need a student`}
              {doneCount > 0 && ` · ${doneCount} uploaded`}
            </span>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => {
                  rows.forEach(r => URL.revokeObjectURL(r.previewUrl));
                  setRows([]);
                }}
                disabled={isUploading}
                className="px-3 py-1.5 border border-gray-200 text-gray-600 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Clear
              </button>
              <button
                onClick={() => void handleUploadAll()}
                disabled={isUploading || readyRows.length === 0}
                className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isUploading ? 'Uploading…' : `Upload ${readyRows.length}`}
              </button>
            </div>
          </div>

          <ul className="divide-y divide-gray-100">
            {rows.map(row => {
              const isDuplicate = Boolean(
                row.studentId && duplicateStudentIds.has(row.studentId) && row.status !== 'done',
              );
              return (
                <li key={row.key} className="flex items-center gap-3 px-4 py-2.5">
                  <img
                    src={row.previewUrl}
                    alt=""
                    className="h-12 w-12 rounded-full object-cover bg-gray-100 flex-shrink-0"
                  />

                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900 truncate" title={row.file.name}>
                      {cleanedStem(row.file.name)}
                    </p>
                    <p className="text-xs text-gray-400">{Math.round(row.file.size / 1024)} KB</p>
                  </div>

                  <div className="w-64 flex-shrink-0">
                    {row.status === 'done' ? (
                      <span className="inline-flex items-center gap-1 text-sm text-green-700">
                        <Check className="h-4 w-4" />
                        Uploaded
                      </span>
                    ) : (
                      <select
                        value={row.studentId ?? ''}
                        onChange={e => assign(row.key, e.target.value || null)}
                        disabled={isUploading}
                        className={`w-full px-2 py-1.5 text-sm border rounded-lg bg-white ${
                          row.studentId ? 'border-gray-200' : 'border-amber-300 bg-amber-50'
                        }`}
                      >
                        <option value="">— pick a student —</option>
                        {students.map(s => (
                          <option key={s.id} value={s.id}>
                            {studentLabel(s)}
                            {s.photo_path ? ' (has photo)' : ''}
                          </option>
                        ))}
                      </select>
                    )}

                    {row.status === 'error' && (
                      <p className="mt-1 text-xs text-red-600">{row.error}</p>
                    )}
                    {row.status !== 'done' && row.ambiguousIds.length > 0 && (
                      <p className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                        Matches {row.ambiguousIds.length} students — pick one
                      </p>
                    )}
                    {row.status !== 'done' && row.fuzzy && (
                      <p className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                        Close match, not exact — check this one
                      </p>
                    )}
                    {isDuplicate && (
                      <p className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                        Another file targets this student
                      </p>
                    )}
                    {row.status !== 'done' && !row.studentId && row.ambiguousIds.length === 0 && (
                      <p className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                        <ImageOff className="h-3 w-3 flex-shrink-0" />
                        No student matched this filename
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => removeRow(row.key)}
                    disabled={isUploading}
                    aria-label={`Remove ${row.file.name}`}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
